import { useEffect } from 'react'
import { useBasket } from '../state/BasketContext'
import { useTranslation } from '../i18n/LanguageContext'
import { NutrientProgressBar } from './NutrientProgressBar'
import type { NutrientAlgorithm } from '../types'
import { translateNutrient, translateNutrientCategory } from '../i18n/nutrientTranslations'

export function IndicatorsView() {
  const {
    scoreResult,
    customConstraints,
    updateNutrientConstraint,
    resetNutrientConstraint,
    selectedNutrientCategories,
    hideIgnoredNutrients,
    setSelectedNutrientCategories,
    setHideIgnoredNutrients,
    filteredIndicators
  } = useBasket()
  const { t, lang } = useTranslation()

  const indicators = scoreResult.indicators
  const allCategories = [...new Set(indicators.map(indicator => indicator.category))]

  useEffect(() => {
    if (allCategories.length > 0 && selectedNutrientCategories.size === 0) {
      setSelectedNutrientCategories(new Set(allCategories))
    }
  }, [allCategories.length, selectedNutrientCategories.size, setSelectedNutrientCategories])

  const toggleCategory = (category: string) => {
    const newSelectedCategories = new Set(selectedNutrientCategories)
    if (newSelectedCategories.has(category)) {
      newSelectedCategories.delete(category)
      if (newSelectedCategories.size === 0) {
        setSelectedNutrientCategories(new Set(allCategories))
        return
      }
    } else {
      newSelectedCategories.add(category)
    }
    setSelectedNutrientCategories(newSelectedCategories)
  }

  const formatValue = (value: number) => {
    if (value < 0.01) return '<0.01'
    if (value < 1) return value.toFixed(2)
    if (value < 10) return value.toFixed(1)
    return Math.round(value).toString()
  }

  const isCustomized = (nutrientId: number) => {
    return customConstraints[nutrientId] !== undefined
  }

  const handleEdit = (nutrientId: number, field: string, value: string) => {
    if (field === 'algorithm') {
      updateNutrientConstraint(nutrientId, { algorithm: value as NutrientAlgorithm })
    } else {
      const numValue = value === '' ? undefined : parseFloat(value)
      if (numValue !== undefined && !isNaN(numValue)) {
        updateNutrientConstraint(nutrientId, { [field]: numValue })
      } else if (value === '') {
        updateNutrientConstraint(nutrientId, { [field]: undefined })
      }
    }
  }

  const handleReset = (nutrientId: number) => {
    resetNutrientConstraint(nutrientId)
  }

  const algorithmOptions: NutrientAlgorithm[] = [
    'reachMinDontExceedMax',
    'reachMin',
    'dontExceedMax',
    'ignore'
  ]

  const getAlgorithmDisplayName = (algorithm: NutrientAlgorithm): string => {
    return t(`algorithm.${algorithm}`)
  }

  const isFieldRelevant = (algorithm: NutrientAlgorithm, field: 'min' | 'target' | 'max'): boolean => {
    switch (algorithm) {
      case 'reachMinDontExceedMax':
        return true
      case 'reachMin':
        return field !== 'max'
      case 'dontExceedMax':
        return field !== 'min'
      case 'ignore':
        return false
    }
  }

  return (
    <div className="h-100 d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center mb-3 p-2 bg-light rounded-2 border">
        <h4 className="h5 mb-0">
          <i className="bi bi-speedometer2 me-2"></i>
          {t('indicators.title')}
        </h4>
        <div className="d-flex align-items-center gap-2 text-muted small">
          <span>{t('indicators.nutrientsLabel')} <strong className="text-dark">
            {filteredIndicators.length}
            {indicators.length !== filteredIndicators.length && (
              <span>/{indicators.length}</span>
            )}
          </strong></span>
          <span className="text-muted">|</span>
          <span>{t('indicators.okLabel')} <strong className="text-success">{filteredIndicators.filter(i => i.passing).length}/{filteredIndicators.length}</strong></span>
          <span className="text-muted">|</span>
          <span>{t('indicators.successLabel')} <strong className="text-primary">
            {filteredIndicators.length > 0
              ? `${Math.round((filteredIndicators.filter(i => i.passing).length / filteredIndicators.length) * 100)}%`
              : '0%'
            }
          </strong></span>
          {Object.keys(customConstraints).length > 0 && (
            <>
              <span className="text-muted">|</span>
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={() => {
                  Object.keys(customConstraints).forEach(nutrientId => {
                    resetNutrientConstraint(parseInt(nutrientId))
                  })
                }}
                title={t('indicators.resetCustomTooltip')}
              >
                <i className="bi bi-arrow-counterclockwise me-1"></i>{t('indicators.resetCustomLabel')}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="mb-3">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div className="d-flex flex-wrap align-items-center gap-2">
            {allCategories.map(category => {
              const localizedCategory = translateNutrientCategory(category, lang)
              const tooltipKey = selectedNutrientCategories.has(category)
                ? 'indicators.toggleCategory.hide'
                : 'indicators.toggleCategory.show'
              return (
                <button
                  key={category}
                  className={`btn btn-sm ${selectedNutrientCategories.has(category) ? 'btn-success' : 'btn-outline-secondary'}`}
                  onClick={() => toggleCategory(category)}
                  title={t(tooltipKey, { category: localizedCategory })}
                >
                  <i className={`bi ${selectedNutrientCategories.has(category) ? 'bi-check-square-fill' : 'bi-square'} me-1`}></i>
                  {localizedCategory}
                </button>
              )
            })}
          </div>
          <button
            className={`btn btn-sm ${hideIgnoredNutrients ? 'btn-success' : 'btn-outline-secondary'}`}
            onClick={() => setHideIgnoredNutrients(!hideIgnoredNutrients)}
            title={hideIgnoredNutrients ? t('indicators.hideIgnoredTooltip') : t('indicators.showAllNutrientsTooltip')}
          >
            <i className={`bi ${hideIgnoredNutrients ? 'bi-check-square-fill' : 'bi-square'} me-1`}></i>
            {t('indicators.hideIgnored')}
          </button>
        </div>
      </div>

      <div className="flex-grow-1 overflow-auto">
        {filteredIndicators.length === 0 ? (
          <div className="text-center text-muted py-4">
            <i className="bi bi-speedometer2 display-6 d-block mb-3 opacity-50"></i>
            <p>{t('indicators.empty')}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0 kajchi-table-nowrap">
              <thead className="table-light sticky-top">
                <tr>
                  <th>{t('indicators.column.nutrient')}</th>
                  <th>{t('indicators.column.category')}</th>
                  <th style={{ width: '190px' }}>{t('indicators.column.progress')}</th>
                  <th>{t('indicators.column.value')}</th>
                  <th style={{ width: '190px' }}>{t('indicators.column.algorithm')}</th>
                  <th>{t('indicators.column.min')}</th>
                  <th>{t('indicators.column.target')}</th>
                  <th>{t('indicators.column.max')}</th>
                  <th className="text-center">{t('indicators.column.ok')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredIndicators.map((indicator) => {
                  return (
                    <tr key={indicator.nutrientId} className={indicator.passing ? 'table-success' : ''}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="fw-medium flex-grow-1">{translateNutrient(indicator.name, lang)}</div>
                          {isCustomized(indicator.nutrientId) && (
                            <button
                              className="btn btn-outline-secondary btn-sm ms-1"
                              onClick={() => handleReset(indicator.nutrientId)}
                              title={t('indicators.tooltip.resetDefault')}
                              style={{ fontSize: '10px', padding: '1px 4px' }}
                            >
                              ↺
                            </button>
                          )}
                        </div>
                      </td>

                      <td>
                        <small className="text-muted">{translateNutrientCategory(indicator.category, lang)}</small>
                      </td>

                      <td className="align-middle">
                        <NutrientProgressBar indicator={indicator} />
                      </td>

                      <td>
                        <strong>{formatValue(indicator.value)}</strong>
                        <small className="text-muted ms-1">{indicator.unit}</small>
                      </td>

                      <td>
                        <select
                          className={`form-select form-select-sm ${isCustomized(indicator.nutrientId) ? 'border-primary' : ''}`}
                          value={indicator.algorithm}
                          onChange={(e) => handleEdit(indicator.nutrientId, 'algorithm', e.target.value)}
                          style={{ fontSize: '11px', minWidth: '100px' }}
                        >
                          {algorithmOptions.map(alg => (
                            <option key={alg} value={alg}>
                              {getAlgorithmDisplayName(alg)}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td>
                        <input
                          type="number"
                          className={`form-control form-control-sm ${isCustomized(indicator.nutrientId) ? 'border-primary' : ''}`}
                          value={isFieldRelevant(indicator.algorithm, 'min') ? (indicator.min ?? '') : ''}
                          onChange={(e) => handleEdit(indicator.nutrientId, 'min', e.target.value)}
                          placeholder="—"
                          style={{ fontSize: '11px', width: '70px' }}
                          step="any"
                          disabled={!isFieldRelevant(indicator.algorithm, 'min')}
                        />
                      </td>

                      <td>
                        <input
                          type="number"
                          className={`form-control form-control-sm ${isCustomized(indicator.nutrientId) ? 'border-primary' : ''}`}
                          value={isFieldRelevant(indicator.algorithm, 'target') ? (indicator.target ?? '') : ''}
                          onChange={(e) => handleEdit(indicator.nutrientId, 'target', e.target.value)}
                          placeholder="—"
                          style={{ fontSize: '11px', width: '70px' }}
                          step="any"
                          disabled={!isFieldRelevant(indicator.algorithm, 'target')}
                        />
                      </td>

                      <td>
                        <input
                          type="number"
                          className={`form-control form-control-sm ${isCustomized(indicator.nutrientId) ? 'border-primary' : ''}`}
                          value={isFieldRelevant(indicator.algorithm, 'max') ? (indicator.max ?? '') : ''}
                          onChange={(e) => handleEdit(indicator.nutrientId, 'max', e.target.value)}
                          placeholder="—"
                          style={{ fontSize: '11px', width: '70px' }}
                          step="any"
                          disabled={!isFieldRelevant(indicator.algorithm, 'max')}
                        />
                      </td>

                      <td className="text-center">
                        {indicator.passing ? (
                          <i className="bi bi-check-circle-fill text-success"></i>
                        ) : (
                          <i className="bi bi-exclamation-triangle-fill text-warning"></i>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
